/**
 * Regras do ciclo de tentativas de distribuição de VoIP que a interface precisa respeitar.
 *
 * Ficam fora do componente porque são puras e porque é aqui que mora o contrato dos comandos:
 * identificação obrigatória e autoria da oferta. Afrouxar qualquer uma das duas transforma o
 * comando num no-op silencioso no backend, que é o modo de falha que esta mudança elimina.
 */

/**
 * Identificação obrigatória da tentativa nos comandos do ciclo. O backend recusa o pedido sem
 * os três campos, então a ausência de tentativa devolve null: melhor não pedir do que enviar
 * um payload parcial.
 *
 * @param tentativa objeto `tentativa` recebido no evento `voip.1`
 * @returns {{id_conteudo_atendimento: number, call_sid: string, numero_tentativa: number}|null}
 */
export function identificacaoTentativa(tentativa) {
  if (!tentativa || !tentativa.idConteudoAtendimento || !tentativa.callSid || !tentativa.numero) {
    return null;
  }
  return {
    id_conteudo_atendimento: tentativa.idConteudoAtendimento,
    call_sid: tentativa.callSid,
    numero_tentativa: tentativa.numero
  };
}

/**
 * A oferta pertence a um ciclo de tentativas de distribuição VoIP.
 *
 * Distingue "ligação recebida com ciclo" (evento `voip.1`, cujo `tentativa` carrega o `callSid`
 * da chamada) de fluxos sem ciclo — transferência entre atendentes e ligação ativa — que não
 * trazem `tentativa`. É o que o aceite usa para não cair no caminho de aceite direto numa
 * oferta com ciclo mas identificação incompleta: conectar ali registraria atendimento por fora
 * do ciclo (que o backend recusa com 409). Defesa redundante com essa recusa do backend.
 *
 * O vínculo é o `callSid`: um objeto `tentativa` sem ele não identifica uma chamada do ciclo.
 *
 * @param tentativa objeto `tentativa` recebido no evento `voip.1`
 * @returns {boolean}
 */
export function temCicloDeTentativas(tentativa) {
  return !!tentativa && !!tentativa.callSid;
}

/**
 * O evento da oferta é entregue a todos os usuários elegíveis, mas só o atendente ofertado
 * pode redistribuir a chamada. Sem atendente ofertado (encaminhamento tipo N) não existe
 * gatilho de interface: a expiração depende exclusivamente do agendamento da fila.
 */
export function ehAtendenteOfertado(tentativa, idUsuario) {
  return !!tentativa
    && tentativa.atendenteOfertado != null
    && Number(tentativa.atendenteOfertado) === Number(idUsuario);
}

/**
 * A oferta que está sendo exibida passou para outro atendente.
 *
 * O backend não emite evento dedicado a quem perdeu a oferta: numa redistribuição o único
 * sinal que chega é o `voip.1` da tentativa seguinte, entregue a todos os elegíveis. Por isso
 * este é o ponto em que a exibição local precisa encerrar — sem ele o modal de quem não
 * respondeu fica aberto sobre uma oferta que já não é dele.
 *
 * O `callSid` amarra o evento à chamada exibida, e o número crescente descarta a reemissão de
 * uma tentativa anterior, que fecharia o modal de quem acabou de receber a oferta.
 *
 * @param tentativaExibida objeto `tentativa` da oferta no modal
 * @param tentativaRecebida objeto `tentativa` do `voip.1` recém-chegado
 */
export function ofertaPerdidaParaOutroAtendente(tentativaExibida, tentativaRecebida, idUsuario) {
  if (!tentativaExibida || !tentativaRecebida || !tentativaExibida.callSid) {
    return false;
  }
  return tentativaRecebida.callSid === tentativaExibida.callSid
    && Number(tentativaRecebida.numero) > Number(tentativaExibida.numero)
    && !ehAtendenteOfertado(tentativaRecebida, idUsuario);
}

/**
 * O atendente já recebeu esta chamada e não deve voltar a ser incomodado por ela.
 *
 * A oferta de transbordo é entregue a todos os elegíveis, então a exclusão de quem já recebeu
 * a chamada só existe como campo no payload — cabe aqui aplicá-la. Ausência do campo significa
 * "não excluir ninguém", que é o comportamento de toda oferta que não vem de transbordo.
 *
 * Distinto de `idsUsuariosQualificados`: qualificação é quem pode receber, exclusão é quem já
 * recebeu. O modal aplica os dois.
 *
 * @param idsUsuariosDesconsiderar campo `idsUsuariosDesconsiderar` do evento `voip.1`
 */
export function ehAtendenteDesconsiderado(idsUsuariosDesconsiderar, idUsuario) {
  if (!Array.isArray(idsUsuariosDesconsiderar)) {
    return false;
  }
  return idsUsuariosDesconsiderar.some((id) => Number(id) === Number(idUsuario));
}

/**
 * Segundos até a dispensa local de uma oferta sem atendente ofertado, ou null quando não se
 * aplica. Cobre transbordo e oferta coletiva, que são o mesmo caso: ninguém é o destinatário.
 *
 * Existe como fallback a falhas da Fila Conecta. O gatilho redundante de redistribuição é
 * proibido aqui — a spec não o aceita para tentativa sem atendente ofertado, e o backend
 * recusaria por autoria — então, sem esta dispensa, o modal ficaria aberto indefinidamente
 * quando a expiração agendada não fosse executada.
 *
 * O prazo é o mesmo campo do gatilho redundante: tempo restante desta oferta já somado à
 * margem, calculado pelo servidor. Não é a configuração do inquilino lida do localStorage, que
 * é um retrato tirado no login, não conhece o tempo restante desta oferta e reintroduziria no
 * navegador o cálculo de prazo que o ciclo de tentativas tirou de lá de propósito. A margem é o
 * que dá precedência ao evento do backend quando a fila está operando normalmente.
 *
 * A condição é da oferta, não de quem pergunta: uma oferta individual tem destinatário e o
 * fechamento dela continua sendo do evento que o backend emitir, para qualquer usuário.
 *
 * @param tentativa objeto `tentativa` recebido no evento `voip.1`
 */
export function segundosAteDispensaOferta(tentativa) {
  if (!tentativa || tentativa.segundosAteGatilhoRedundante == null) {
    return null;
  }
  if (tentativa.atendenteOfertado != null) {
    return null;
  }
  return Number(tentativa.segundosAteGatilhoRedundante);
}
